// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ga from 'app/common/lib/ga';
import i18n from 'app/common/lib/i18n';
import Toast from 'app/common/react/ui/Toast';
import withAuthenticatedAction from 'app/common/react/components/limitedLogin/withAuthenticatedAction';
import { track } from 'app/common/lib/react/analytics';
import { type State as ReduxState } from 'app/common/redux/types';
import withShowToast, {
  type InjectedProps as ToastProps,
} from 'app/common/lib/react/withShowToast';
import { Box, Button, Pulsar, Touchable } from 'gestalt';
import { compose, type Dispatch } from 'redux';
import { follow, unfollow } from 'app/common/redux/actions/board/follow';

const followGACategory = 'board_follow';
const unfollowGACategory = 'board_unfollow';

type AuthActionProps = {|
  authAction: Function, // TODO: this could use a more precise type
|};

type ReduxStateProps = {|
  isFollowed: boolean,
|};

type ReduxDispatchProps = {|
  follow: (id: string) => void,
  unfollow: (id: string) => void,
|};

type OwnProps = {|
  boardId: string,
  boardUrl: string,
  boardImage: string,
  boardName: string,
  isBoardLandingPage?: boolean,
  isFollowed: boolean,
  ownerId: string,
  size?: 'sm' | 'md' | 'lg',
  showPulsar: boolean,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
|};

type ConnectedProps = {|
  ...OwnProps,
  ...ReduxStateProps,
  ...ReduxDispatchProps,
|};

type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
  ...ReduxDispatchProps,
  ...AuthActionProps,
  ...ToastProps,
|};

export class BoardFollowButton extends PureComponent<Props> {
  followUnfollow = () => {
    const {
      boardId,
      boardImage,
      boardUrl,
      boardName,
      follow,
      hideAllToasts,
      isFollowed,
      showToast,
      unfollow,
    } = this.props;

    if (isFollowed) {
      unfollow(boardId);
    } else {
      follow(boardId);
      // keeping Flow happy, remove if hideAllToasts type can't be void
      if (hideAllToasts) {
        hideAllToasts();
      }
      // keeping Flow happy, remove if showToast type can't be void
      if (showToast) {
        showToast(({ onHide }) => (
          <Toast
            onHide={onHide}
            text={[
              i18n._(
                'Now following',
                'text that comes before a board/user name in toast on homefeed',
              ),
              boardName,
            ]}
            href={boardUrl}
            imageUrl={boardImage}
          />
        ));
      }
    }
  };

  logBoardFollowClick = (isBoardFollowed: boolean) => {
    const { boardId, isBoardLandingPage, viewParameter } = this.props;

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.FLOWED_BOARD,
      element: isBoardFollowed
        ? PCONST.thrift.ElementType.BOARD_UNFOLLOW
        : PCONST.thrift.ElementType.BOARD_FOLLOW,
      objectId: boardId,
      view: isBoardLandingPage ? PCONST.thrift.ViewType.BOARD : PCONST.thrift.ViewType.USER,
      viewParameter,
    });
  };

  handleFollow = ({ event }: { event: SyntheticMouseEvent<> }) => {
    event.preventDefault();
    event.stopPropagation();

    const { authAction, isFollowed } = this.props;
    const GACategory = isFollowed ? unfollowGACategory : followGACategory;

    authAction(this.followUnfollow)();

    this.logBoardFollowClick(isFollowed);
    ga.trackEvent(GACategory, PCONST.GA_ACTION_CLICK);
  };

  handlePulsarClick = (event: any) => {
    this.handleFollow(event);
  };

  render() {
    const { isFollowed, showPulsar, size } = this.props;
    const buttonText = isFollowed ? i18n._('Following') : i18n._('Follow');

    return (
      <Box
        position="relative"
        display="flex"
        data-test-id={`board-${!isFollowed ? 'follow' : 'unfollow'}-button`}
      >
        <Button
          color={isFollowed ? 'gray' : 'red'}
          inline
          onClick={this.handleFollow}
          size={size}
          text={buttonText}
        />
        {showPulsar && (
          <Touchable onTouch={this.handlePulsarClick}>
            <Box
              position="absolute"
              display="inlineBlock"
              dangerouslySetInlineStyle={{ __style: { marginLeft: -86, marginTop: -28 } }}
            >
              <Pulsar paused={isFollowed} />
            </Box>
          </Touchable>
        )}
      </Box>
    );
  }
}

const mapStateToProps = (
  { boards }: ReduxState,
  { boardId, isFollowed }: OwnProps,
): ReduxStateProps => {
  const board = boards.content[boardId];
  return { isFollowed: board ? Boolean(board.followed_by_me) : isFollowed };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps => ({
  follow: (id) => dispatch(follow(id)),
  unfollow: (id) => dispatch(unfollow(id)),
});

export default compose(
  connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withAuthenticatedAction,
  // $FlowFixMe - these HOCs need some Flow love
  withShowToast,
)(BoardFollowButton);
